import Phaser from 'phaser';
import { createProceduralArt } from '../ui/ProceduralArt';
import { ensureNavalFallbackTextures } from '../ui/NavalProceduralArt';

export interface SpritesheetManifestEntry {
  path: string;
  frameWidth: number;
  frameHeight: number;
  animations?: string[];
}

export interface TilesetManifestEntry {
  path: string;
  tileWidth?: number;
  tileHeight?: number;
  columns?: number;
  rows?: number;
}

export interface AssetManifest {
  images?: Record<string, string>;
  spritesheets?: Record<string, SpritesheetManifestEntry>;
  atlases?: Record<string, { image: string; json: string }>;
  tilesets?: Record<string, TilesetManifestEntry>;
  audio?: Record<string, string>;
  music?: Record<string, string>;
  vfx?: Record<string, SpritesheetManifestEntry>;
  portraits?: Record<string, string | { path: string; faction?: string }>;
  factions?: Record<string, string | { path: string; group?: string }>;
  ui?: Record<string, string>;
  aliases?: Record<string, string>;
}

export class AssetRegistry {
  static readonly instance = new AssetRegistry();

  manifest: AssetManifest = {};
  missing = new Set<string>();
  unavailable = new Set<string>();

  loadManifest(data?: AssetManifest) {
    this.manifest = data ?? { images: {}, spritesheets: {}, atlases: {}, tilesets: {}, audio: {}, music: {}, vfx: {}, portraits: {}, factions: {}, ui: {}, aliases: {} };
  }

  resolve(key: string) { return this.manifest.aliases?.[key] ?? key; }

  image(key: string, fallback?: string) {
    const resolved = this.resolve(key);
    const semanticFallback = fallback ?? this.fallbackForImage(resolved);
    if (this.unavailable.has(resolved)) return semanticFallback;
    if (this.manifest.images?.[resolved] || this.manifest.ui?.[resolved] || this.manifest.factions?.[resolved] || this.manifest.portraits?.[resolved]) return resolved;
    this.missing.add(key);
    return semanticFallback;
  }

  spritesheet(key: string, fallback = 'fallback.soldier.green') {
    const resolved = this.resolve(key);
    const semanticFallback = this.isNavalKey(resolved) ? 'fallback.ship.neutral' : fallback;
    if (this.unavailable.has(resolved)) return semanticFallback;
    if (this.manifest.spritesheets?.[resolved] || this.manifest.vfx?.[resolved]) return resolved;
    this.missing.add(key);
    return semanticFallback;
  }

  audio(key: string) {
    const resolved = this.resolve(key);
    if (this.unavailable.has(resolved)) return undefined;
    if (this.manifest.audio?.[resolved] || this.manifest.music?.[resolved]) return resolved;
    this.missing.add(key);
    return undefined;
  }

  getImagePath(key: string) {
    const resolved = this.resolve(key);
    if (this.unavailable.has(resolved)) return undefined;
    return this.manifest.images?.[resolved]
      ?? this.manifest.ui?.[resolved]
      ?? this.pathFromObject(this.manifest.factions?.[resolved])
      ?? this.pathFromObject(this.manifest.portraits?.[resolved]);
  }

  markUnavailable(key: string) { this.unavailable.add(this.resolve(key)); }

  ensureFallbackTextures(scene: Phaser.Scene) {
    createProceduralArt(scene);
    ensureNavalFallbackTextures(scene);
  }

  private pathFromObject(value: string | { path: string } | undefined) { return typeof value === 'string' ? value : value?.path; }

  private isNavalKey(key: string) {
    return /(?:navy|ship|war_boat|junk|dhow|galleon|fluyt|galley|transport)/.test(key);
  }

  private fallbackForImage(key: string) {
    if (key === 'title_screen' || key === 'main_menu_background' || key === 'key_art_main') return 'fallback.title';
    if (key.startsWith('strategic_map_') || key.startsWith('tileset_strategic_')) return 'fallback.strategic-map';
    if (key === 'background_naval_battle' || key.startsWith('tileset_naval_')) return 'fallback.naval-water';
    if (key === 'campaign_hud_frame' || key.includes('button_set')) return 'fallback.hud';
    if (key.startsWith('portrait_')) return 'fallback.portrait';
    if (key.includes('_banner_') || key.endsWith('_emblem') || key.endsWith('_emblem_original')) return 'fallback.banner';
    if (key.startsWith('event_') || key.startsWith('loading_screen_') || key.startsWith('key_art_')) return 'fallback.art';
    return 'fallback.panel';
  }
}